import { isEmptyString, isNotEmptyString } from '@vy/utils';
import type { FC } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { AddErpIdDialog } from '../../../../../../../components/integrations/AddErpIdDialog';
import { VyDialog } from '../../../../../../../components/new-design/feedback/VyDialog';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import { useNetSuiteSyncWizard } from '../hooks/use-net-suite-sync-wizard';
import { useNetSuiteSyncWizardStep } from '../hooks/use-net-suite-sync-wizard-step';
import { CustomerConnectionStep } from '../steps/CustomerConnectionStep';
import { ProductSelectionStep } from '../steps/ProductSelectionStep';
import {
  SubsidiarySelectionStep,
  type SubsidiarySelectionStepRef,
} from '../steps/SubsidiarySelectionStep';
import {
  DIALOG_CONTENT_STYLE,
  DIALOG_LABEL,
  DIALOG_LABEL_DESCRIPTION,
  INITIAL_DIALOG_STATE,
  WIZARD_STEPS,
  type AddErpIdDialogState,
  type NetSuiteSyncWizardProps,
  type WizardStep,
} from '../types';

export const NetSuiteSyncWizardContent: FC<NetSuiteSyncWizardProps> = ({
  open,
  onClose,
  contractId,
  customerId,
  isSynced,
  customerErpId: initialCustomerErpId,
}) => {
  const queryClient = useQueryClient();
  const [addErpIdDialogState, setAddErpIdDialogState] = useState<AddErpIdDialogState>(INITIAL_DIALOG_STATE);
  const subsidiaryStepRef = useRef<SubsidiarySelectionStepRef>(null);

  const needsCustomerConnectionRef = useRef(isEmptyString(initialCustomerErpId));
  const needsCustomerConnection = needsCustomerConnectionRef.current;

  const getMemo = useCallback(() => subsidiaryStepRef.current?.getMemo() ?? '', []);

  const {
    step,
    selectedProductsIds,
    setSelectedProductsIds,
    selectedSubsidiary,
    setSelectedSubsidiary,
    selectedCustomer,
    setSelectedCustomer,
    handleNext,
    handleBack,
    handleSync,
  } = useNetSuiteSyncWizard({ contractId, customerId, isSynced, onClose, needsCustomerConnection, getMemo });

  const currentErpId = useMemo(() => {
    if (isNotEmptyString(initialCustomerErpId)) {
      return initialCustomerErpId;
    }

    return selectedCustomer?.erpId;
  }, [initialCustomerErpId, selectedCustomer]);

  const stepSequence: WizardStep[] = useMemo(() => {
    if (needsCustomerConnection) {
      return [
        WIZARD_STEPS.CUSTOMER_CONNECTION,
        WIZARD_STEPS.PRODUCT_SELECTION,
        WIZARD_STEPS.SUBSIDIARY_AND_MEMO,
      ];
    }

    return [WIZARD_STEPS.PRODUCT_SELECTION, WIZARD_STEPS.SUBSIDIARY_AND_MEMO];
  }, [needsCustomerConnection]);

  const currentStep = stepSequence[step];
  const isLastStep = step === stepSequence.length - 1;
  const isFirstStep = step === 0;

  const handleOpenAddErpIdDialog = useCallback((productId: string) => {
    setAddErpIdDialogState({ open: true, productId });
  }, []);

  const handleCloseAddErpIdDialog = useCallback(() => {
    setAddErpIdDialogState(INITIAL_DIALOG_STATE);
  }, []);

  const handleSaveErpId = useCallback(async () => {
    handleCloseAddErpIdDialog();
    await queryClient.invalidateQueries(queryKeys.contractProductsOverviews(contractId));
  }, [handleCloseAddErpIdDialog, queryClient, contractId]);

  const { isNextDisabled, ctaText } = useNetSuiteSyncWizardStep({
    currentStep,
    isLastStep,
    isSynced,
    selectedCustomer,
    selectedProductsIds,
    selectedSubsidiary,
  });

  const isAddErpIdDialogOpen = addErpIdDialogState.open;

  const renderStepContent = () => {
    switch (currentStep) {
      case WIZARD_STEPS.CUSTOMER_CONNECTION:
        return (
          <CustomerConnectionStep
            selectedCustomer={selectedCustomer}
            onCustomerSelect={setSelectedCustomer}
          />
        );

      case WIZARD_STEPS.PRODUCT_SELECTION:
        return (
          <ProductSelectionStep
            contractId={contractId}
            selectedProductsIds={selectedProductsIds}
            setSelectedProductsIds={setSelectedProductsIds}
            onAddErpId={handleOpenAddErpIdDialog}
          />
        );

      case WIZARD_STEPS.SUBSIDIARY_AND_MEMO:
        return (
          <SubsidiarySelectionStep
            ref={subsidiaryStepRef}
            erpId={currentErpId ?? ''}
            selectedSubsidiary={selectedSubsidiary}
            onSubsidiarySelect={setSelectedSubsidiary}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <VyDialog
        open={open && !isAddErpIdDialogOpen}
        onClose={onClose}
        label={DIALOG_LABEL}
        labelDescription={DIALOG_LABEL_DESCRIPTION}
        cancelButtonText="Cancel"
        ctaText={ctaText}
        ctaDisabled={isNextDisabled}
        onSubmit={isLastStep ? handleSync : handleNext}
        navigateBack={!isFirstStep ? handleBack : undefined}
        disableDividers
        dialogContentProps={{
          style: DIALOG_CONTENT_STYLE,
        }}
      >
        {renderStepContent()}
      </VyDialog>

      <AddErpIdDialog
        open={isAddErpIdDialogOpen}
        onClose={handleCloseAddErpIdDialog}
        onSave={handleSaveErpId}
        productId={addErpIdDialogState.productId}
        customerId={customerId}
      />
    </>
  );
};

